'use client';

import { useLogger } from 'next-axiom';
import { useTransition } from 'react';
import { useElevarEvents } from '../../elevar';
import { serializeCartItemToElevarProduct } from '../../elevar/elevar-helpers';
import { usePreviousPage } from '../../lib/hooks/use-previous-page';
import { removeItemFromCartAction } from '../actions';
import { useCart } from '../context';
import { useClearCartCache } from './use-clear-cart-cache';

export function useRemoveFromCart({ domain }: { domain: string }) {
  const { clearCartCache } = useClearCartCache();
  const { updateCartItem, loading, setLoading } = useCart();
  const [_, startTransition] = useTransition();
  const previousPage = usePreviousPage();
  const { trackRemoveFromCart } = useElevarEvents();
  const log = useLogger().with({ location: 'useRemoveFromCart' });

  const removeItem = (merchandiseId: string) => {
    setLoading(true);

    const success = updateCartItem({
      merchandiseId,
      updateType: 'delete',
    });

    // Only proceed if validation passed
    if (!success) {
      setLoading(false);
      return;
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    startTransition(async () => {
      try {
        const res = await removeItemFromCartAction({ merchandiseId });

        if (!res) {
          log.error('removeItemFromCartAction returned undefined');
          clearCartCache();
          return;
        }

        if (res.serverError) {
          log.error('serverError', { error: res.serverError });
          clearCartCache();
          return;
        }

        if (res.validationErrors) {
          log.error('validationErrors', { error: res.validationErrors });
          clearCartCache();
          return;
        }

        if (!res.data) {
          log.error('actionResponse.data is undefined after error checks');
          clearCartCache();
          return;
        }

        if (res.data.lineItem?.node) {
          trackRemoveFromCart({
            list: previousPage?.collection ?? '',
            products: [
              serializeCartItemToElevarProduct({
                item: res.data.lineItem.node,
                domain,
                list: previousPage?.collection ?? '',
                quantity: res.data.lineItem.node.quantity,
              }),
            ],
          });
        }
        clearCartCache();
      } finally {
        setLoading(false);
      }
    });
  };

  return { removeItem, loading };
}
